import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Card,
  Switch,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  styled,
  tableCellClasses,
  Typography,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { paddingCardsTables } from '../../../commons';
import {
  hasPemissions,
  usarFuncionesComposicion,
  corregirTexto,
  firstLetterUpperCase,
} from '../../../commons/utils/helpers';
import {
  getAssignmentsStore,
  storeDeleteAssignment,
  storeActiveAssignment,
  getAssignmentsByPagination,
  getAssignmentsBySearch,
} from '../../../store/ratings/thunks';
import { useTableState } from '../../../hooks/useTableState';
import { SinDatos } from '../../../commons/generics/SinDatos';
import { GenericTableHead, HeadCell } from '../../../commons/generics/GenericTableHead';
import { ClampedText } from '../../../commons/generics/ClampedText';
import { StatusChip } from '../../../commons/generics/StatusChip';
import { ModalConfirmDeactivation } from '../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../components/modals/ModalConfirmRestore';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: 15,
    fontWeight: 700,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: 500,
    color: '#2e263de6',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 },
}));

const headCells: HeadCell[] = [
  { id: 'index', label: '#', sortable: false },
  { id: 'deviceManagement.name', label: 'Dispositivo', sortable: true },
  { id: 'surveys', label: 'Encuesta', sortable: false },
  { id: 'lastSyncAt', label: 'Última Sincronización', sortable: true },
  { id: 'status', label: 'Estado', sortable: true },
  { id: 'actions', label: 'Acciones', align: 'center', sortable: false },
];

interface Props {
  onEdit: (assignment: any) => void;
  onView?: (assignment: any) => void;
}

export const TableAsignarEncuestas = ({ onEdit, onView }: Props) => {
  const dispatch = useDispatch();
  const { assignments_list }: any = useSelector((state: any) => state.ratingsDevices);

  const {
    dataTableList,
    canView,
    setCanView,
    canEdit,
    setCanEdit,
    canDelete,
    setCanDelete,
    canRestore,
    setCanRestore,
    openElem,
    setOpenElem,
    checked,
    setChecked,
    searchTerm,
    setSearchTerm,
    order,
    orderBy,
    handleSwitchChange,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    pagePagination,
    itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    handleRequestSort,
    updatePaginationFromList,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    ResponseComponents,
    handleResponse,
    getSortedAndFilteredList,
    isServerSide,
  } = useTableState('qiasignarencuestas');

  // Local State
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    updatePaginationFromList(assignments_list);
  }, [assignments_list]);

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    changePaginationPageHook(event, value);
    const response = await dispatch(getAssignmentsByPagination(value, checked ? 0 : 1) as any);
    await handleResponse(response, 'listar asignaciones', false);
  };

  const handleSearch = async (param: string) => {
    setSearchTerm(param);
    setInputValue(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) => getAssignmentsBySearch(p, checked ? 0 : 1),
      getData,
      null,
      'buscar asignaciones'
    );
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;
    if (searchTerm) {
      handleSearchHook(
        searchTerm,
        dispatch,
        (p: string) => getAssignmentsBySearch(p, status),
        () => getData(status),
        null,
        'buscar asignaciones'
      );
    } else {
      getData(status);
    }
  };

  const onResetSearch = () => {
    setInputValue('');
    handleResetSearchHook(() => getData());
  };

  useEffect(() => {
    checkPermissions();
    getData();
  }, []);

  const checkPermissions = async () => {
    const view = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.asignar.encuestas',
      'view'
    );
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.asignar.encuestas',
      'edit'
    );
    if (edit) setCanEdit(true);

    const del = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.asignar.encuestas',
      'delete'
    );
    if (del) setCanDelete(true);

    const res = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.asignar.encuestas',
      'restore'
    );
    if (res) setCanRestore(true);
  };

  const getData = async (currentStatus?: number) => {
    const status = currentStatus !== undefined ? currentStatus : checked ? 0 : 1;
    const response = await dispatch(getAssignmentsStore(status));
    await handleResponse(response, 'obtener asignaciones', false);
  };

  const handleDelete = (row: any) => {
    setOpenElem(row);
    setOpenModalDeactivation(true);
  };

  const handleRestore = (row: any) => {
    setOpenElem(row);
    setOpenModalRestore(true);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeDeleteAssignment(openElem.id, inputValue, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'desactivar asignación', true);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeActiveAssignment(openElem.id, inputValue, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'restaurar asignación', true);
  };

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['deviceManagement.name'],
    'status',
    checked ? null : 1
  );

  if (!hasPermission) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <Typography color="error">No tienes permisos para ver este contenido.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 5 }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar asignación..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivas (Asignaciones)
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 700 }}>
            <GenericTableHead
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {sortedList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <SinDatos />
                  </TableCell>
                </TableRow>
              ) : (
                sortedList
                  .slice(
                    isServerSide ? 0 : page * rowsPerPage,
                    isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                  )
                  .map((row: any, index: any) => (
                    <StyledTableRow key={row.id} hover>
                      <StyledTableCell>
                        <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {page * rowsPerPage + index + 1}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell sx={{ fontWeight: 600 }}>
                        <ClampedText
                          text={usarFuncionesComposicion(
                            corregirTexto,
                            firstLetterUpperCase,
                            row.deviceManagement?.name || 'N/A'
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {row.surveys && row.surveys.length > 0 ? (
                          row.surveys.length === 1 ? (
                            <ClampedText text={row.surveys[0].label} />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: 'primary.main',
                                border: '1px solid',
                                borderColor: 'primary.main',
                                display: 'inline-block',
                                px: 1,
                                py: 0.2,
                                borderRadius: 1,
                              }}
                            >
                              {row.surveys.length} Encuestas
                            </Typography>
                          )
                        ) : (
                          <ClampedText text={null} />
                        )}
                      </StyledTableCell>

                      <StyledTableCell>
                        <ClampedText
                          text={
                            row.lastSyncAt ? new Date(row.lastSyncAt).toLocaleString() : 'Nunca'
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusChip status={row.status} />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                            <IconButton
                              size="small"
                              onClick={() => canView && onView && onView(row)}
                              disabled={!canView}
                              color="info"
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          <span
                            style={{
                              cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => canEdit && row.status === 1 && onEdit(row)}
                              disabled={!canEdit || row.status !== 1}
                              color="primary"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          {row.status === 1 ? (
                            <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canDelete && handleDelete(row)}
                                disabled={!canDelete}
                                color="error"
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          ) : (
                            <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canRestore && handleRestore(row)}
                                disabled={!canRestore}
                                color="success"
                              >
                                <RestoreOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          )}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              )}
            </TableBody>
          </Table>

          {dataTableList.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={dataTableList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página"
            />
          )}

          {dataTableList.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 1,
                padding: 2.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Pagination
                sx={{ justifyContent: 'center' }}
                color="primary"
                count={itemCountPagination}
                page={pagePagination}
                onChange={changePaginationPage}
                showFirstButton
                showLastButton
                shape="circular"
              />
            </Box>
          )}
        </TableContainer>
      </Card>

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        onConfirm={confirmDeactivate}
      />
      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        onConfirm={confirmRestore}
      />
    </Box>
  );
};
